import React from "react";
import { FiAlertTriangle } from "react-icons/fi";

export type DataRemovalConfirmProps = {
	/** Les reculs que la confirmation va retirer, un libellé par ligne (« Sujet : détail »). */
	items: string[];
	/** Vrai quand le serveur a refusé parce que la base a bougé depuis la validation. */
	listChanged?: boolean;
	/** Faux quand la liste n'a pas d'empreinte serveur : la confirmation est alors impossible. */
	confirmable?: boolean;
	/** L'opérateur a coché la confirmation du retrait. */
	accepted: boolean;
	onAcceptedChange: (accepted: boolean) => void;
	disabled?: boolean;
};

/**
 * Encadré « Ce classeur retire des données déjà publiées », posé À CÔTÉ du bouton de
 * confirmation. Il existe parce que le remplacement d'un classeur est intégral : un fichier
 * qui ne porte plus certains mois les retire du portail. C'est parfois une erreur (un fichier
 * trop ancien), parfois une correction voulue (le 16/09/2026, la Banque retirait juin et juillet
 * de trois sujets en attendant leur correction).
 *
 * <p>L'ancien refus s'affichait en tête de page, hors de vue de qui venait de cliquer en bas :
 * l'opérateur ne voyait qu'une notification passagère et ne trouvait pas comment confirmer. Ici,
 * la liste, la case à cocher et le bouton sont au même endroit, et la case est obligatoire.</p>
 */
export const DataRemovalConfirm = React.forwardRef<HTMLElement, DataRemovalConfirmProps>(
	({ items, listChanged, confirmable = true, accepted, onAcceptedChange, disabled }, ref) => (
		<section
			ref={ref}
			data-testid="data-loss-guard"
			aria-labelledby="data-removal-title"
			className="mt-4 rounded-xl border-2 border-rose-300 bg-rose-50 p-4"
		>
			<div className="flex items-start gap-3">
				<div className="rounded-lg bg-rose-100 p-2 text-rose-700">
					<FiAlertTriangle className="h-5 w-5" />
				</div>
				<div className="min-w-0 flex-1">
					<h3 id="data-removal-title" className="text-sm font-bold text-rose-900">
						Ce classeur retire des données déjà publiées
					</h3>
					{listChanged && (
						<p className="mt-1 text-sm font-semibold text-rose-900" role="alert">
							La liste a changé depuis la validation : relisez-la avant de confirmer.
						</p>
					)}
					<p className="mt-1 text-sm text-rose-800">
						Le remplacement est intégral. Ces données disparaîtront de l'aperçu dès la confirmation, puis des pages
						publiques à la mise en production. S'il s'agit d'une correction voulue, cochez la case. Sinon, vérifiez le
						fichier avant d'aller plus loin.
					</p>
					<p className="mt-1 text-xs text-rose-800">
						Les cartes de la page d'accueil qui lisent l'inflation et les taux d'intérêt sont, elles, recalculées dès la
						confirmation.
					</p>
					<ul className="mt-3 space-y-1">
						{items.map((item) => (
							<li key={item} className="rounded-lg bg-white/80 px-3 py-1.5 text-xs text-rose-900">
								{item}
							</li>
						))}
					</ul>
					{!confirmable && (
						<p className="mt-3 text-sm font-semibold text-rose-900" role="alert">
							Cette liste ne peut pas être confirmée telle quelle : validez de nouveau le classeur.
						</p>
					)}
					<label className="mt-3 flex cursor-pointer items-start gap-2 text-sm font-semibold text-rose-900">
						<input
							type="checkbox"
							checked={accepted}
							disabled={disabled || !confirmable}
							onChange={(event) => onAcceptedChange(event.target.checked)}
							className="mt-0.5 h-4 w-4 accent-rose-700"
						/>
						Je confirme le retrait de ces données
					</label>
				</div>
			</div>
		</section>
	)
);

DataRemovalConfirm.displayName = "DataRemovalConfirm";

export default DataRemovalConfirm;
